import React from "react"
import { Card, Grid, Typography } from "@material-ui/core"
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles"
import customers from "../../../assets/images/company/customers.png"
import customersMobile from "../../../assets/images/company/customers_mo.png"
import usePlatform from "../../../hooks/usePlatform"
import useScrollImpressionLog from "../../../hooks/useScrollImpressionLog"
import { Locale } from "../../../locale"
import testimonials, { Testimonial } from "../../../fixtures/testimonial"
import Carousel from "../../../components/Carousel/Carousel"
import { useVariation } from "@hackler/react-sdk"
import { TESTIMONIAL_TEST_KEY } from "../../../abTestKey"
import ArrowIcon from "../../../assets/images/icon/ArrowIcon"

interface StyleProps {
  isMobile: boolean
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: ({ isMobile }: StyleProps) => (isMobile ? "16px 0 18px" : "20px 0 38px"),
      width: "100%",
      margin: 0,
    },
    h2: {
      fontSize: "22px",
      [theme.breakpoints.up("md")]: {
        fontSize: "40px",
      },
    },
    caption: {
      fontSize: "14px",
      color: "rgba(0,0,0,0.6)",
      [theme.breakpoints.up("md")]: {
        fontSize: "20px",
      },
    },
    buttonWrapper: {
      justifyContent: "center",
    },
    customerLogo: {
      width: 312,
      "& > img": {
        width: "100%",
      },
      [theme.breakpoints.up("md")]: {
        width: 1114,
      },
    },
    testimonialSection: {
      [theme.breakpoints.up("md")]: {
        margin: "40px 0",
      },
    },
  })
)

export default function ParticipatingCompanies() {
  const testimonialVariation = useVariation(TESTIMONIAL_TEST_KEY)
  const { isMobile } = usePlatform()
  const { lang } = Locale.getDefault()
  const classes = useStyles({ isMobile })
  const { ref: customerLogoRef } = useScrollImpressionLog({
    message: "1_0: 핵클 고객사",
    eventKey: "homepage_home_section_1_0_impression",
  })
  const { ref: testimonialRef } = useScrollImpressionLog({
    message: "1_5: 핵클 고객사 추천서",
    eventKey: "homepage_home_section_1_10_impression",
  })

  return (
    <div ref={customerLogoRef}>
      <Grid className={classes.root} spacing={isMobile ? 4 : 7} container justify={"center"} alignItems={"center"}>
        <Grid item xs={12}>
          <Grid container item spacing={2}>
            <Grid item xs={12} style={{ textAlign: "center" }}>
              <Typography variant={"h2"} className={classes.h2}>
                {lang === "ko" && <strong>1,000개 이상의 기업이 {isMobile && <br />}핵클을 선택했습니다</strong>}
                {lang === "en" && <strong>1,000+ companies using Hackle</strong>}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid container alignItems={"center"} justify={"center"}>
            <Grid item className={classes.customerLogo}>
              <img src={isMobile ? customersMobile : customers} />
            </Grid>
          </Grid>
        </Grid>
        {testimonialVariation === "B" && (
          <Grid item xs={12} className={classes.testimonialSection} ref={testimonialRef}>
            <Grid container justify={"center"}>
              <Carousel
                slides={testimonials.map((testimonials, idx) => (
                  <TestimonialCard key={idx} {...testimonials} />
                ))}
                prevElement={isMobile ? <></> : <ArrowIcon.LeftCircleOutline size={40} fillColor={"#2962FF"} />}
                nextElement={isMobile ? <></> : <ArrowIcon.RightCircleOutline size={40} fillColor={"#2962FF"} />}
                lgSlidePerView={3}
                smSlidePerView={1}
                interval={400}
                showExtraSlides={isMobile}
                touchEnabled={isMobile}
              />
            </Grid>
          </Grid>
        )}
      </Grid>
    </div>
  )
}

const useCardStyles = makeStyles<Theme>((theme) => ({
  link: {
    textDecoration: "none",
    cursor: "pointer",
    "&:hover > div": {
      boxShadow: "0 0 0 1px inset #2962FF",
    },
  },
  card: {
    boxShadow: "0px 0px 12px rgba(41, 98, 255, 0.15)",
    borderRadius: "24px",
    display: "flex",
    flexDirection: "column",
    width: "240px",
    height: "280px",
    padding: "20px",
    wordBreak: "keep-all",
    "& *": {
      fontSize: "12px",
    },
    [theme.breakpoints.up("md")]: {
      width: "384px",
      height: "440px",
      padding: "32px",
      "& *": {
        fontSize: "18px",
      },
    },
  },
  cardInner: {
    position: "relative",
    height: "100%",
  },
  imageWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    "& > img": {
      width: "60px",
    },
    height: "60px",
    [theme.breakpoints.up("md")]: {
      "& > img": {
        width: "120px",
      },
      height: "120px",
    },
  },
  writer: {
    display: "flex",
    flexDirection: "column",
    position: "absolute",
    bottom: 0,
    color: "rgba(0, 0, 0, 0.6);",
  },
}))

function TestimonialCard({ link, content, company, writer }: Testimonial) {
  // TODO: Locale.getDefault()의 반환 타입을 좁힌 뒤, 해당 삼항식은 제거
  const lang = Locale.getDefault().lang === "ko" ? "ko" : "en"
  const classes = useCardStyles()
  const companyName = company.name[lang]
  const writerName = writer.name[lang]

  const testimonialCard = (
    <Card className={classes.card}>
      <div className={classes.cardInner}>
        <div className={classes.imageWrapper}>
          <img src={company.logoSrc} alt={companyName} />
        </div>
        <Typography>{content[lang]}</Typography>
        <Typography className={classes.writer}>
          {writerName && <span>{writerName}</span>}
          <span>
            {companyName} {writer.job}
          </span>
        </Typography>
      </div>
    </Card>
  )

  if (!link) return testimonialCard

  return (
    <a className={classes.link} target="_blank">
      {testimonialCard}
    </a>
  )
}
